import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useDispatch} from "react-redux";
import {useLocation, useNavigate} from "react-router-dom";
import {setMessage} from "../../../system/app-slice";
import {projectMeetingsApi} from "../../../models/project/project-meetings-api";
import MeetingDrawer from "./meeting-drawer";
import MeetingFormDialog from "./meeting-form-dialog";
import {MY_MEETINGS_CHANGED} from "./my-meetings-store";
import {errorText} from "./meetings-labels";

// The meeting sidebar opened from a meeting card in «کارها» or from a notification (boss's task, 2026-10-27;
// docs/tasks/30-meeting-cards.md). It works for anyone invited, for the owner and the creator — also when they cannot
// open the meeting's project. Everyone sees the meeting; only the owner and the creator can change it.

export const MY_MEETINGS_OPEN = "my-meetings:open";
export const openMyMeeting = (key) => window.dispatchEvent(new CustomEvent(MY_MEETINGS_OPEN, {detail: {key}}));

// mounted once on the page; opens on a card click (event) and on «?meeting=<key>» (notification link)
export default function MeetingSidebarHost() {
    const location = useLocation();
    const navigate = useNavigate();
    const [key, setKey] = useState(() => new URLSearchParams(location.search).get("meeting"));
    const fromLink = new URLSearchParams(location.search).get("meeting");

    useEffect(() => {
        if (fromLink) setKey(fromLink);
    }, [fromLink]);
    useEffect(() => {
        const open = (event) => setKey(event.detail?.key || null);
        window.addEventListener(MY_MEETINGS_OPEN, open);
        return () => window.removeEventListener(MY_MEETINGS_OPEN, open);
    }, []);

    const close = useCallback(() => {
        setKey(null);
        if (new URLSearchParams(location.search).get("meeting")) navigate(location.pathname, {replace: true});
    }, [location.pathname, location.search, navigate]);

    return key ? <MeetingSidebar meetingKey={key} onHide={close}/> : null;
}

function MeetingSidebar({meetingKey, onHide}) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [detail, setDetail] = useState(null);
    const [failed, setFailed] = useState(false);
    const [formOpen, setFormOpen] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setDetail(null);
        setFailed(false);
        projectMeetingsApi.myMeeting(meetingKey)
            .then((data) => { if (!cancelled) setDetail(data); })
            .catch(() => { if (!cancelled) setFailed(true); });
        return () => { cancelled = true; };
    }, [meetingKey]);

    useEffect(() => {
        if (failed) {
            dispatch(setMessage({severity: "error", content: "این جلسه پیدا نشد یا به آن دسترسی ندارید."}));
            onHide();
        }
    }, [failed, dispatch, onHide]);

    // the drawer reads the project's tab shape; the meeting sidebar gives it the same from its own data
    const tab = useMemo(() => (detail ? {
        people: detail.people,
        decisions: [],
        meetings: [detail.meeting],
        tasks: [],
        targets: [],
        today: detail.today,
        permissions: {canDelete: false, canEdit: detail.permissions.canEdit},
    } : null), [detail]);

    const changed = (next) => {
        setDetail(next);
        window.dispatchEvent(new Event(MY_MEETINGS_CHANGED));
    };
    const toast = (content, severity = "success") => dispatch(setMessage({severity, content}));

    const actions = {
        // status, attendance, minutes and agenda topics from the panel: errors are shown here
        patchMeeting: async (key, patch) => {
            try {
                changed(await projectMeetingsApi.updateMyMeeting(key, patch));
                toast(patch.status ? "وضعیت جلسه ذخیره شد." : patch.notes !== undefined ? "صورت‌جلسه ذخیره شد." : "تغییر ذخیره شد.");
            } catch (error) {
                toast(errorText(error), "error");
                throw error;
            }
        },
        editMeeting: () => setFormOpen(true),
        openProject: (projectKey) => navigate(`/projects/${projectKey}?tab=meetings`),
    };

    const save = async (values) => {
        // the form shows its own error
        changed(await projectMeetingsApi.updateMyMeeting(meetingKey, values));
        toast("تغییرات جلسه ذخیره شد.");
        setFormOpen(false);
    };

    if (!detail) return null;
    return (
        <>
            <MeetingDrawer standalone visible meeting={detail.meeting} tab={tab} brief={detail.decisions} project={detail.project} onHide={onHide} actions={actions}/>
            <MeetingFormDialog visible={formOpen} onHide={() => setFormOpen(false)} meeting={detail.meeting} people={[]} knownPeople={detail.people} onSave={save}/>
        </>
    );
}
